import { Eye, Send } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { Panel } from '../parts';
import { NotificationPreview } from './NotificationPreview';
import { ResultCard } from './ResultCard';
import type { BroadcastForm } from './useBroadcastForm';

export function BroadcastActionsPanel({ form }: { form: BroadcastForm }) {
  const {
    title,
    body,
    ctaLabel,
    destKind,
    busy,
    result,
    error,
    confirming,
    estSize,
    canSubmit,
    setConfirming,
    run
  } = form;

  return (
    <>
      <Panel title="Preview" bodyClassName="p-4 md:p-5">
        <NotificationPreview title={title} body={body} cta={destKind !== 'none' ? ctaLabel || 'See more' : null} />
      </Panel>

      <Panel bodyClassName="p-4 md:p-5">
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => run(true)}
            disabled={!canSubmit}
            className={cn(
              'tap flex items-center justify-center gap-2 rounded-2xl border border-black/10 bg-card py-3 text-[14px] font-bold text-ink',
              !canSubmit && 'opacity-40'
            )}
          >
            <Eye size={16} /> {busy === 'preview' ? 'Checking…' : 'Preview audience'}
          </button>

          {!confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={!canSubmit}
              className={cn(
                'tap flex items-center justify-center gap-2 rounded-2xl bg-night py-3 text-[14px] font-bold text-white',
                !canSubmit && 'opacity-40'
              )}
            >
              <Send size={16} /> Send…
            </button>
          ) : (
            <div className="rounded-2xl border border-danger/30 bg-miss/[0.06] p-3">
              <p className="text-[13px] font-semibold text-ink">
                Send to {result?.dryRun ? result.recipients : `~${estSize}`} player
                {(result?.dryRun ? result.recipients : estSize) === 1 ? '' : 's'} now? This can’t be undone.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => run(false)}
                  disabled={busy !== false}
                  className={cn(
                    'tap flex flex-1 items-center justify-center gap-2 rounded-xl bg-danger py-2.5 text-[13px] font-bold text-white',
                    busy !== false && 'opacity-50'
                  )}
                >
                  <Send size={15} /> {busy === 'send' ? 'Sending…' : 'Send for real'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="tap rounded-xl border border-black/10 px-4 py-2.5 text-[13px] font-semibold text-ink2"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {error && <p className="text-[12px] font-semibold text-danger">{error}</p>}

          {result && <ResultCard result={result} />}
        </div>
      </Panel>
    </>
  );
}
